import React, { useState, useEffect } from "react";
import { useMutation, useLazyQuery } from "@apollo/client";
import { Button, styled, Snackbar, Alert, Box } from "@mui/material";
import { useNavigate } from "react-router";
import GSCConnection from "./GSCConnection";
import BackButton from "../BackButton/BackButton";
import GAConnection from "./GAConnection";
import GCSSites from "./GCSSites";
import SiteSelector from "./SiteSelector";
import GAPropertySelector from "./GAPropertySelector";
import CombinedInsights from "./CombinedInsights";
import WelcomeCard from "./GaWelcomeCard";
import { GET_GSC_AUTH_STATUS } from "../../gql/queries/gsc";
import { GET_GA_AUTH_STATUS } from "../../gql/queries/ga4";
import { DISCONNECT_GSC } from "../../gql/mutations/gsc";
import { DISCONNECT_GA } from "../../gql/mutations/ga4";

const GoogleAnalyticsWrapper = styled("div")(({ theme }) => ({
  minHeight: "100vh",
  paddingLeft: "20px",
  paddingRight: "20px",
  backgroundColor: theme.palette.background.default,

  "& .container": {
    maxWidth: "1200px",
    margin: "0",
    backgroundColor: theme.palette.background.default,
  },

  "& .header": {
    display: "flex",
    alignItems: "center",
    padding: "20px 0",
    position: "relative",
  },

  "& .headerText": {
    margin: "0 auto",
    textAlign: "center",
  },

  "& .header h1": {
    fontSize: "2.5rem",
    color: theme.palette.text.primary,
    marginBottom: "10px",
  },

  "& .header p": {
    fontSize: "1rem",
    color: theme.palette.text.secondary,
  },

  "& .loading": {
    textAlign: "center",
    padding: "40px",
    fontSize: "1.2rem",
    color: theme.palette.text.secondary,
  },

  "& .connectionsGrid": {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
    gap: "10px",
  },

  "& .tab-navigation": {
    display: "flex",
    gap: 6,
    padding: 6,
    borderRadius: 14,
    background: theme.palette.primary.light,
    marginTop: 30,
    // marginBottom: 20,
    flexWrap: "wrap",
  },

  "& .tab-btn": {
    flex: 1,
    minWidth: 140,
    padding: "10px 16px",
    borderRadius: 10,
    fontWeight: 500,
    textTransform: "none",
    color: "theme.palette.text.secondary",
    background: "transparent",
    transition: "all 0.2s ease",

    "&.active": {
      background: theme.palette.primary.main,
      color: "white",
      fontWeight: 600,
      boxShadow: theme.shadows[1],
    },

    "&:hover": {
      background: theme.palette.primary.dark,
    },

    "&.Mui-disabled": {
      opacity: 0.45,
      cursor: "not-allowed",
    },
  },

  "@media (max-width:768px)": {
    "& .connectionsGrid": {
      gridTemplateColumns: "1fr",
    },
  },
}));

function GoogleAnalyticsMain() {
  const [authStatus, setAuthStatus] = useState(null);
  const [gaAuthStatus, setGaAuthStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(""); // "gsc" | "ga" | "combined"
  const isGscConnected = !!authStatus?.authenticated;
  const isGaConnected = !!gaAuthStatus?.authenticated;

  const canAccessCombined = isGscConnected && isGaConnected;
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info", // "info" | "warning" | "error" | "success"
  });

  const [fetchGscAuthStatus] = useLazyQuery(GET_GSC_AUTH_STATUS, {
    fetchPolicy: "network-only",
  });

  const [fetchGaAuthStatus] = useLazyQuery(GET_GA_AUTH_STATUS, {
    fetchPolicy: "network-only",
  });

  const [disconnectGsc] = useMutation(DISCONNECT_GSC);
  const [disconnectGa] = useMutation(DISCONNECT_GA);

  useEffect(() => {
    checkAuthStatus();
    checkGAAuthStatus();

    // Check for OAuth callbacks
    const urlParams = new URLSearchParams(window.location.search);
    const authParam = urlParams.get("auth");
    const gaAuthParam = urlParams.get("ga_auth");
    const errorParam = urlParams.get("error");
    if (authParam === "success") {
      console.log("true");
      checkAuthStatus();
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (gaAuthParam === "success") {
      const email = urlParams.get("email");

      checkGAAuthStatus().then(status => {
        if (!status?.authenticated) {
          setTimeout(() => checkGAAuthStatus(), 500);
          setTimeout(() => checkGAAuthStatus(), 1500);
          setTimeout(() => checkGAAuthStatus(), 3000);
        }
      });

      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (errorParam === "auth_failed" || errorParam === "ga_auth_failed") {
      const errorMessage = urlParams.get("message") || "Authentication failed. Please try again.";

      if (!errorMessage.includes("expired") && !errorMessage.includes("Authorization code")) {
        alert(`Authentication Error: ${errorMessage}`);
      } else {
        console.log("Authorization code expired - user should retry");
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // const checkAuthStatus = async () => {
  //   try {
  //     // Create AbortController for timeout
  //     const controller = new AbortController();
  //     const timeoutId = setTimeout(() => controller.abort(), 5000);

  //     const response = await fetch("http://localhost:8000/api/auth/gsc/status", {
  //       signal: controller.signal,
  //     });

  //     clearTimeout(timeoutId);

  //     if (!response.ok) {
  //       throw new Error(`HTTP error! status: ${response.status}`);
  //     }

  //     const data = await response.json();
  //     setAuthStatus(data);
  //   } catch (error) {
  //     console.error("Error checking auth status:", error);
  //     // Set default unauthenticated state on error
  //     setAuthStatus({ authenticated: false, email: null });
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const checkAuthStatus = async () => {
    try {
      const { data } = await fetchGscAuthStatus({
        variables: {
          email: authStatus?.email || null,
        },
      });

      const status = data?.gscAuthStatus?.status;

      if (!status) {
        throw new Error("Invalid GSC auth status response");
      }

      setAuthStatus(status);
    } catch (error) {
      console.error("Error checking GSC auth status:", error);
      setAuthStatus({ authenticated: false, email: null });
    } finally {
      setLoading(false);
    }
  };

  // const checkGAAuthStatus = async () => {
  //   try {
  //     const response = await fetch("http://localhost:8000/api/auth/ga/status");
  //     if (!response.ok) {
  //       throw new Error(`HTTP error! status: ${response.status}`);
  //     }
  //     const data = await response.json();
  //     console.log("GA Auth Status:", data);
  //     setGaAuthStatus(data);
  //     return data;
  //   } catch (error) {
  //     console.error("Error checking GA auth status:", error);
  //     setGaAuthStatus({ authenticated: false, email: null });
  //     return { authenticated: false, email: null };
  //   }
  // };

  const checkGAAuthStatus = async () => {
    try {
      const { data } = await fetchGaAuthStatus({
        variables: {
          email: gaAuthStatus?.email || null,
        },
      });

      const status = data?.gaAuthStatus?.status;

      if (!status) {
        throw new Error("Invalid GA auth status response");
      }

      console.log("GA Auth Status:", status);
      setGaAuthStatus(status);
      return status;
    } catch (error) {
      console.error("Error checking GA auth status:", error);
      const fallback = { authenticated: false, email: null };
      setGaAuthStatus(fallback);
      return fallback;
    }
  };

  // const handleDisconnect = async () => {
  //   try {
  //     const response = await fetch("http://localhost:8000/api/auth/gsc/disconnect", {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //       },
  //       body: JSON.stringify({ email: authStatus?.email }),
  //     });
  //     const data = await response.json();
  //     if (data.success) {
  //       setAuthStatus({ authenticated: false });
  //     }
  //   } catch (error) {
  //     console.error("Error disconnecting:", error);
  //   }
  // };
  const handleDisconnect = async () => {
    try {
      const { data } = await disconnectGsc({
        variables: {
          email: authStatus?.email || null,
        },
      });

      if (data?.disconnectGsc?.success) {
        setAuthStatus({ authenticated: false, email: null });
      }
    } catch (error) {
      console.error("Error disconnecting GSC:", error);
    }
  };
  const handleGADisconnect = async () => {
    try {
      const { data } = await disconnectGa({
        variables: {
          email: gaAuthStatus?.email || null,
        },
      });

      if (data?.disconnectGa?.success) {
        console.log("object", data?.disconnectGa);
        setGaAuthStatus({ authenticated: false, email: null });
      }
    } catch (error) {
      console.error("Error disconnecting GA:", error);
    }
  };

  const showToast = (message, severity = "warning") => {
    setToast({
      open: true,
      message,
      severity,
    });
  };

  if (loading) {
    return (
      // <Box className="app">
      <GoogleAnalyticsWrapper>
        <Box>
          <Box className="loading">Loading...</Box>
        </Box>
      </GoogleAnalyticsWrapper>
      // </Box>
    );
  }

  return (
    // <Box className="app">
    <GoogleAnalyticsWrapper>
      <Box>
        <header className="header">
          <WelcomeCard />
        </header>

        <Box className="connectionsGrid">
          <GSCConnection authStatus={authStatus} onConnect={checkAuthStatus} onDisconnect={handleDisconnect} />
          <GAConnection authStatus={gaAuthStatus} onConnect={checkGAAuthStatus} onDisconnect={handleGADisconnect} />
        </Box>
        <Box className="tab-navigation">
          <Button
            className={`tab-btn ${activeTab === "gsc" ? "active" : ""}`}
            disabled={!isGscConnected}
            onClick={() => {
              if (!isGscConnected) {
                showToast("Please connect Google Search Console to access GSC features.");
                return;
              }
              setActiveTab("gsc");
            }}
          >
            📊 GSC
          </Button>

          <Button
            className={`tab-btn ${activeTab === "ga" ? "active" : ""}`}
            disabled={!isGaConnected}
            onClick={() => {
              if (!isGaConnected) {
                showToast("Please connect Google Analytics to access GA features.");
                return;
              }
              setActiveTab("ga");
            }}
          >
            📈 GA
          </Button>

          <Button
            className={`tab-btn ${activeTab === "combined" ? "active" : ""}`}
            disabled={!canAccessCombined}
            onClick={() => {
              if (!canAccessCombined) {
                showToast("Please connect both Google Search Console and Google Analytics to view Combined Insights.");
                return;
              }
              setActiveTab("combined");
            }}
          >
            🔗 Combined Insights
          </Button>
        </Box>

        {authStatus?.authenticated && activeTab === "gsc" && (
          <>
            <GCSSites email={authStatus.email} />
            <SiteSelector email={authStatus.email} />
          </>
        )}

        {gaAuthStatus?.authenticated && activeTab === "ga" && <GAPropertySelector email={gaAuthStatus.email} authStatus={gaAuthStatus} />}

        {/* Combined Insights - Shows when both GSC and GA4 are connected */}
        {authStatus?.authenticated && gaAuthStatus?.authenticated && activeTab === "combined" && <CombinedInsights gscEmail={authStatus.email} gaEmail={gaAuthStatus.email} />}
      </Box>
      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast(prev => ({ ...prev, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </GoogleAnalyticsWrapper>
    // {/* </Box> */}
  );
}

export default GoogleAnalyticsMain;
